import { ColumnsType } from 'antd/es/table';
import { message, Table } from 'antd';
import TitleBar from '../../../../components/TitleBar';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Footer } from '../../../../components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CREATE_PRODUCT_GROUP,
  DELETE_PRODUCT_GROUP,
  EDIT_PRODUCT_GROUP,
  FETCH_PRODUCT_GROUP_BY_DIVISION,
} from '../../queries';
import { SPECIAL_CHAR_REGEX } from '../../../../utils/regex';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../../utils';
import FinancialData from '../../../../components/common/FinancialData';
import { formFields } from './formFields';
import { baseColumns } from './exportColumns';
import { useUserContext } from '../../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../../components/error-handling';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import { ErrorMessages } from '../../../../constants';

type GetAllProductGroup = {
  costOfSales: number;
  grossMargin: number;
  isDefault: boolean;
  uuid: string;
  name: string;
  percentage: number;
  revenue: number;
};

type DataReturned = {
  getAllProductGroupsByDivision: GetAllProductGroup[];
};

function ProductGroupFinancials() {
  const params = useParams();
  const corporateId = params.corporateId!;
  const corporateName = params.corporateName!;
  const divisionId = params.divisionId!;
  const divisionName = params.divisionName!;
  const navigate = useNavigate();

  const { canViewFinancials } = useUserContext();

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_PRODUCT_GROUP_BY_DIVISION,
    {
      variables: {
        uuid: params.divisionId,
      },
    },
  );

  const [createProductGroup] = useMutation(CREATE_PRODUCT_GROUP, {
    refetchQueries: [`GetProductGroupByDivision`],
  });

  const [updateProductGroup] = useMutation(EDIT_PRODUCT_GROUP, {
    refetchQueries: [`GetProductGroupByDivision`],
    onError: (error) => {
      message.error(
        error instanceof Error && error.message
          ? error.message
          : ErrorMessages.OPERATION_FAILED,
      );
    },
  });

  const [deleteProductGroup] = useMutation(DELETE_PRODUCT_GROUP, {
    refetchQueries: [`GetProductGroupByDivision`],
  });

  const transformedData = data?.getAllProductGroupsByDivision.filter((item) => {
    return !item.isDefault;
  });

  const columns: ColumnsType<GetAllProductGroup> = [
    {
      title: 'Product Group',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.revenue, b.revenue)
        : false,
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: 'Cost of Sales',
      dataIndex: 'costOfSales',
      key: 'costOfSales',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.costOfSales, b.costOfSales)
        : false,
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: 'Gross Margin',
      key: 'grossMargin',
      dataIndex: 'grossMargin',
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.grossMargin, b.grossMargin)
        : false,
      width: 100,
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
  ];

  const extendedActions = (record: any) => {
    const nameParts = record?.name?.split(SPECIAL_CHAR_REGEX);
    const formattedName = nameParts.join('-').toLowerCase();

    return (
      <Button
        type="primary-rounded"
        className="text-sm"
        onClick={() =>
          navigate(
            `/${corporateName}/${corporateId}/${divisionName}/${divisionId}/${formattedName}/${record.uuid}`,
          )
        }
      >
        Products
      </Button>
    );
  };

  const tableSummary = () => {
    const defaultRow = data?.getAllProductGroupsByDivision?.filter((item) => {
      return item.isDefault;
    })!;

    if (!defaultRow) return;

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} className="custom-table--column text-sm">
          {defaultRow[0].name}
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={1}
          align="right"
          className="custom-table--column text-sm"
        >
          <FinancialData text={defaultRow[0].revenue} />
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={2}
          align="right"
          className="custom-table--column text-sm"
        >
          <FinancialData text={defaultRow[0].costOfSales} />
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={3}
          align="right"
          className="custom-table--column text-sm"
        >
          <FinancialData text={defaultRow[0].grossMargin} />
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  const modifyVariables = ({
    values,
    type,
    currentRecord,
  }: ModifyVariablesArgs) => {
    const revenue = parseFloat(String(values.revenue).replace(/,/g, ''));
    const costOfSales = parseFloat(
      String(values.costOfSales).replace(/,/g, ''),
    );

    if (type === 'create') {
      return {
        divisionFinancial: { uuid: params.divisionId },
        revenue: revenue,
        costOfSales: costOfSales,
        grossMargin: 0,
        name: values.name,
      };
    }

    return {
      revenue: revenue,
      costOfSales: costOfSales,
      grossMargin: currentRecord.grossMargin,
      name: values.name,
    };
  };

  return (
    <>
      <TitleUpdater title="Product Groups Financials" />
      <TitleBar
        title="Data Inputs"
        subtitle={`Financials // ${divisionName}`}
        brief="Within the selected division, add the major products groups and associated financials
                      below. This step is optional and will make the analysis MEO can perform more accurate. To provide
                      the most granular level of detail, you can also add specific products within each product group by
                      clicking the button next to each product group."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Product Groups Financials"
          columns={columns}
          data={transformedData}
          loading={loading}
          editable
          summary={tableSummary}
          formFields={formFields}
          createGQL={createProductGroup}
          updateGQL={updateProductGroup}
          deleteGQL={deleteProductGroup}
          modifyVariables={modifyVariables}
          editableOptions={{
            create: canViewFinancials,
            update: canViewFinancials,
            delete: canViewFinancials,
          }}
          inlineEditing
          extendActions={extendedActions}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={`/${corporateName}/${corporateId}`} />
    </>
  );
}

export default ProductGroupFinancials;
